import { Type, WritableSignal, signal } from '@angular/core';
import {
  property,
  subclass
} from '@arcgis/core/core/accessorSupport/decorators';
import {
  BaseLayerProperties,
  ExtendedMap,
  JepLayer
} from '@jeppesen-foreflight/jmkw-types';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { TwcTime } from './twc-time';

// Base interfaces
export interface TwcLayerInit {
  timeMap: ExtendedMap<TwcTime>;
  flightLevelValues: string[];
  selectedTime: string;
  selectedFlightLevel: string;
}

export interface TwcLayerProperties
  extends BaseLayerProperties,
    Partial<TwcLayerInit> {}

export interface TwcLayerInterface {
  timeMap: WritableSignal<ExtendedMap<TwcTime>>;
  flightLevelValues: WritableSignal<string[]>;
  selectedTime: string;
  selectedTime$: Observable<string>;
  selectedFlightLevel: string;
  selectedFlightLevel$: Observable<string>;
  updateTimeMap(timeMap: ExtendedMap<TwcTime>, selectedTime?: string): string;
  updateFlightLevels(levels: string[], selectedFlightLevel?: string): string;
}

export type TwcLayerType = JepLayer & TwcLayerInterface;
export type TwcMixinType = typeof TwcMixin<Type<TwcLayerType>>;

// Utility functions
export const initialFlightLevel = (
  flightLevelValues?: string[]
): string | undefined =>
  flightLevelValues?.includes('FL240') ? 'FL240' : flightLevelValues?.[0];

export const initialTime = (timeMap: ExtendedMap<TwcTime>): string =>
  [...timeMap.keys()][0] ?? '';

export const normalizeSelectedTime = (
  timeMap: ExtendedMap<TwcTime>,
  selectedTime?: string,
  currentSelectedTime = ''
): string => {
  if (timeMap.size === 0) return selectedTime || currentSelectedTime;

  const preferredTime =
    selectedTime && timeMap.has(selectedTime)
      ? selectedTime
      : currentSelectedTime;

  return (
    preferredTime && timeMap.has(preferredTime)
      ? preferredTime
      : initialTime(timeMap)
  ) ?? '';
};

export const normalizeSelectedFlightLevel = (
  flightLevelValues: string[],
  selectedFlightLevel?: string,
  currentSelectedFlightLevel = ''
): string => {
  if (flightLevelValues.length === 0) return currentSelectedFlightLevel;

  return selectedFlightLevel && flightLevelValues.includes(selectedFlightLevel)
    ? selectedFlightLevel
    : currentSelectedFlightLevel &&
        flightLevelValues.includes(currentSelectedFlightLevel)
      ? currentSelectedFlightLevel
      : (initialFlightLevel(flightLevelValues) ?? '');
};

/**
 * TWC Layer Mixin - adds weather visualization capabilities to ArcGIS layers
 */
export function TwcMixin<TBase extends Type<JepLayer>>(Base: TBase) {
  @subclass('jmkw.layers.TwcLayer')
  class TwcLayer extends Base implements TwcLayerInterface {
    constructor(...args: any[]) {
      const [
        {
          timeMap,
          flightLevelValues,
          selectedTime,
          selectedFlightLevel,
          ...props
        }
      ] = args;
      super(props);

      if (timeMap) this.timeMap.set(timeMap);
      if (flightLevelValues) this.flightLevelValues.set(flightLevelValues);
      if (selectedTime) this.selectedTime = selectedTime;
      if (selectedFlightLevel) this.selectedFlightLevel = selectedFlightLevel;
    }

    @property()
    timeMap = signal(new ExtendedMap<TwcTime>());

    @property()
    flightLevelValues = signal<string[]>([]);

    private _selectedTime$ = new BehaviorSubject<string>('');

    @property()
    get selectedTime(): string {
      return this._selectedTime$.value;
    }

    set selectedTime(time: string) {
      if (this._selectedTime$.value === time) return;
      this._selectedTime$.next(time);
    }

    get selectedTime$(): Observable<string> {
      return this._selectedTime$.asObservable();
    }

    private _selectedFlightLevel$ = new BehaviorSubject<string>('FL240');

    @property()
    get selectedFlightLevel(): string {
      return this._selectedFlightLevel$.value;
    }

    set selectedFlightLevel(flightLevel: string) {
      if (this._selectedFlightLevel$.value === flightLevel) return;
      this._selectedFlightLevel$.next(flightLevel);
    }

    get selectedFlightLevel$(): Observable<string> {
      return this._selectedFlightLevel$.asObservable();
    }

    updateTimeMap(
      timeMap: ExtendedMap<TwcTime>,
      selectedTime?: string
    ): string {
      if (timeMap.size === 0) return this.selectedTime;

      // Check if timeMap has changed before updating
      const currentTimeMap = this.timeMap();
      const currentKeys = [...currentTimeMap.keys()];
      const newKeys = [...timeMap.keys()];

      const keysChanged = !isEqual(currentKeys, newKeys);

      if (keysChanged) {
        this.timeMap.set(timeMap);
      }

      // Update selectedTime if current selection is invalid
      this.selectedTime = normalizeSelectedTime(
        timeMap,
        selectedTime,
        this.selectedTime
      );

      return this.selectedTime;
    }

    updateFlightLevels(levels: string[], selectedFlightLevel?: string): string {
      if (levels.length === 0) return this.selectedFlightLevel;

      // Check if levels have changed before updating
      const currentLevels = this.flightLevelValues();

      const levelsChanged = !isEqual(currentLevels, levels);

      if (levelsChanged) {
        this.flightLevelValues.set(levels);
      }

      // Update selectedFlightLevel if current selection is invalid
      this.selectedFlightLevel = normalizeSelectedFlightLevel(
        levels,
        selectedFlightLevel,
        this.selectedFlightLevel
      );

      return this.selectedFlightLevel;
    }
  }

  return TwcLayer as unknown as Type<TwcLayerInterface & InstanceType<TBase>>;
}

// Helper function for updating TWC layers
export function updateTwcLayerTimeOptions<L extends TwcLayerInterface>(
  layer: L,
  timeMap: ExtendedMap<TwcTime>
): L {
  layer.updateTimeMap(timeMap);
  return layer;
}
